import axios, { createAxiosInstance } from "./_axios";
import { API } from "./apiEndPoints";
import { refreshAccountCounts } from "@/utils/accountCountUtils";

/** If the first argument is a single "options" object, spread into (account_id, access_id, data). */
const resolveStateUpdateActionArgs = (account_id, access_id, data, defaultData) => {
  if (typeof account_id === "object" && !access_id) {
    const p = account_id;
    return {
      account_id: p.accountId || p.account_id,
      access_id: p.accessId || p.access_id || p.userId || p.user_id,
      data: p.data || defaultData,
    };
  }
  return { account_id, access_id, data };
};

export const getListofUser = (account_id, page = 1, size = 500) => {
  const offset = Math.max(0, (page - 1) * size);
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/access/`,
    params: { page, size, offset, limit: size },
  });
  return result;
};
/**
 * Account access list (v2). Server-side pagination via `limit` / `offset`, optional `search`,
 * `ordering`, and filter params: `access_status` (array, repeated on the wire), `employee`,
 * `permission`.
 */
export const getListofUserV2 = (
  account_id,
  limit,
  offset,
  search,
  filters = {},
  ordering = null
) => {
  const params = {
    limit,
    offset,
  };

  if (search && String(search).trim()) {
    params.search = String(search).trim();
  }

  if (filters.access_status && filters.access_status.length > 0) {
    params.access_status = filters.access_status;
  }

  if (filters.employee === "is_employee" || filters.employee === "not_employee") {
    params.employee = filters.employee;
  }

  const permission = filters.permission;
  if (
    permission === "none" ||
    permission === "owner" ||
    permission === "co-owner" ||
    permission === "admin"
  ) {
    params.permission = permission;
  }

  if (ordering) {
    params.ordering = ordering;
  }

  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/`,
    params,
    paramsSerializer: (p) => {
      const parts = [];
      Object.keys(p).forEach((key) => {
        const value = p[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          });
        } else if (value != null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
};
export const cancelInviteUser = (user_id) => {
  let result = axios()({
    method: "post",
    url: `${API.USERList}${user_id}/cancel-invite/`,
  });
  return result;
};

export const createUser = async (account_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/user/`,
    data: data,
  });
  if (data?.employee_id) {
    await refreshAccountCounts(account_id);
  }
  return result;
};

/**
 * Give access / link user to employee (v2).
 * POST /api/common/v2/account/{account_id}/employee/{employee_id}/user-create-linked/
 */
export const userCreateLinked = async (account_id, employee_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/user-create-linked/`,
    data,
  });
  if (employee_id) {
    await refreshAccountCounts(account_id);
  }
  return result;
};
export const detachEmployeeAccess = async (account_id, employee_id, data = {}) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/detach/`,
    data,
  });
  if (employee_id) {
    await refreshAccountCounts(account_id);
  }
  return result;
};
export const createNewAccountUser = async (account_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/create-user/`,
    data: data,
  });
  return result;
};

export const manageAppAccess = (account_id, user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/access/`,
    data: data,
  });
  return result;
};

export const getAppAccess = (account_id, user_id, data) => {
  let result = axios()({
    method: "get",
    url: `${API.COMMON_V1}account/${account_id}/user/${user_id}/access/`,
    data: data,
  });
  return result;
};

export const changePassword = (user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER}${user_id}/password/`,
    data: data,
  });
  return result;
};

/**
 * Owner/Co-Owner-initiated password reset (Rule 17.3 / MDP-570).
 * POST /api/common/v2/account/{account_id}/access/{access_id}/reset-password/
 * Targets the row's Access, not the currently logged-in user.
 */
export const resetPasswordForAccess = (account_id, access_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/reset-password/`,
  });
  return result;
};

/**
 * Cancel the live pending email change on an Access (Rule 10.6 / ER-11997).
 * POST /api/common/v2/account/{account_id}/access/{access_id}/email-change/cancel/
 */
export const cancelPendingEmailChange = (account_id, access_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/email-change/cancel/`,
  });
  return result;
};

/**
 * Owner/Co-Owner-initiated Welcome wizard reset (MDP-621 / ER-12250).
 * POST /api/common/v2/account/{account_id}/access/{access_id}/welcome-wizard/reset/
 * Targets the row's Access, not the currently logged-in user.
 */
export const resetWelcomeWizardForAccess = (account_id, access_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/welcome-wizard/reset/`,
  });
  return result;
};

/**
 * Onboarding wizard state (ER-12245 / MDP-621).
 * GET /api/common/v2/account/{account_id}/access/{access_id}/welcome-wizard/
 * Returns `{ flow, steps, current_step, total_steps, landing_path }`.
 */
export const getWelcomeWizardState = (account_id, access_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/welcome-wizard/`,
  });
};

/**
 * Record a wizard page action for the signed-in Access (ER-12245 / MDP-621).
 * POST .../welcome-wizard/page/{page_key}/{action}/
 * `action` is `complete`, `skip` or `dismiss`.
 */
export const welcomeWizardPageAction = (account_id, access_id, page_key, action) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/welcome-wizard/page/${page_key}/${action}/`,
  });
};

/** Continue from a wizard page: the page is done and never shown again (rule 6.2). */
export const completeWelcomeWizardPage = (account_id, access_id, page_key) =>
  welcomeWizardPageAction(account_id, access_id, page_key, "complete");

/** Skip for now: the page stays pending and returns at the next sign-in (rule 6.2). */
export const skipWelcomeWizardPage = (account_id, access_id, page_key) =>
  welcomeWizardPageAction(account_id, access_id, page_key, "skip");

/** Do not ask again: the page is never shown again for this Access (rule 6.2). */
export const dismissWelcomeWizardPage = (account_id, access_id, page_key) =>
  welcomeWizardPageAction(account_id, access_id, page_key, "dismiss");

/**
 * Set your profile step (ER-12243 / MDP-621).
 * GET /api/common/v2/account/{account_id}/access/{access_id}/wizard/profile/
 * Returns `{ kind, first_name, last_name, picture, prefilled_from_user_picture }`.
 */
export const getWelcomeWizardProfile = (account_id, access_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/wizard/profile/`,
  });
};

/**
 * Save the Set your profile step. `formData` carries `first_name`, `last_name`
 * and the optional `picture` / `employee_picture` files (rules 5.4-5.5).
 */
export const saveWelcomeWizardProfile = (account_id, access_id, formData) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/access/${access_id}/wizard/profile/`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const userStateUpdateSuspend = async (account_id, access_id, data) => {
  const args = resolveStateUpdateActionArgs(account_id, access_id, data, { state: "suspended" });
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${args.account_id}/access/${args.access_id}/suspend/`,
    data: args.data,
  });
  await refreshAccountCounts(args.account_id);
  return result;
};

export const userResendInvite = (user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER}${user_id}/resend-invite/`,
    data: data,
  });
  return result;
};

export const resendInviteByEmployeeId = (account_id, employee_id, data = {}) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/resend-invite/`,
    data: data,
  });
  return result;
};

export const cancelInviteByEmployeeId = (account_id, employee_id) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/employee/${employee_id}/cancel-invite/`,
  });
  return result;
};

export const userStateUpdateGiveBack = async (account_id, access_id, data) => {
  const args = resolveStateUpdateActionArgs(account_id, access_id, data, { state: "operational" });
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${args.account_id}/access/${args.access_id}/un-suspend/`,
    data: args.data,
  });
  await refreshAccountCounts(args.account_id);
  return result;
};

export const userStateUpdate = (account_id, user_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/`,
    data: data,
  });
  return result;
};

export const userCheckEmail = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/test/email/`,
    data: data,
  });
  return result;
};

export const resetUserPassword = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}reset-password/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const sendNewPasswordData = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}user/reset/password/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const signupWithToken = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}user/0/signup/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const requestInviteRenewal = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}invite/renewal/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const renameUser = (account_id, user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/rename/`,
    data: data,
  });
  return result;
};

export const transferOwnerShip = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/change-owner/`,
    data: data,
  });
  return result;
};

export const partialUpdateUser = (user_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.USER}${user_id}/`,
    data: data,
  });
  return result;
};
export const userChangeEmail = (account_id, access_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.COMMON_V1}slack/${account_id}/access/${access_id}/change-user/`,
    data: data,
  });
  return result;
};
export const getOperatingCountry = (account_id, params = {}) => {
  const { offset = 0, size = 10, limit, search, status } = params;
  const pageSize = limit ?? size;
  const queryParams = { offset, size: pageSize, limit: pageSize };
  if (typeof search === "string" && search.trim()) {
    queryParams.search = search.trim();
  }
  if (Array.isArray(status) && status.length > 0) {
    queryParams.status = status;
  }
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/operating-country/`,
    params: queryParams,
    // Repeat `status` for django-filter MultipleChoiceFilter (?status=active&status=retired).
    paramsSerializer: (p) => {
      const parts = [];
      Object.keys(p).forEach((key) => {
        const value = p[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          });
        } else if (value != null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
  return result;
};
export const getCountryChatterList = (account_id, country_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/operating-country/${country_id}/chatter/`,
    params: { page: page },
  });
  return result;
};
export const getOperatingCurrency = (account_id, params = {}) => {
  const { offset = 0, size = 10, limit, search, status } = params;
  const pageSize = limit ?? size;
  const queryParams = { offset, size: pageSize, limit: pageSize, ordering: "currency_name" };
  if (typeof search === "string" && search.trim()) {
    queryParams.search = search.trim();
  }
  if (Array.isArray(status) && status.length > 0) {
    queryParams.status = status;
  }
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/`,
    params: queryParams,
    // Repeat `status` for django-filter MultipleChoiceFilter (?status=active&status=retired).
    paramsSerializer: (p) => {
      const parts = [];
      Object.keys(p).forEach((key) => {
        const value = p[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          });
        } else if (value != null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
  return result;
};
export const addOperatingCurrency = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/`,
    data: data,
  });
  return result;
};
// Retire / Reactivate an operating currency — backend accepts an active-only
// PATCH (retire => active:false, reactivate => active:true). Retiring the
// reference currency is rejected with CURRENCY_REFERENCE_CANNOT_BE_RETIRED.
export const retireOperatingCurrency = (account_id, id) => {
  return axios()({
    method: "patch",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/`,
    data: { active: false },
  });
};
export const reactivateOperatingCurrency = (account_id, id) => {
  return axios()({
    method: "patch",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/`,
    data: { active: true },
  });
};
// Set as the account reference currency.
export const setOperatingCurrencyAsReference = (account_id, id) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/set-as-reference/`,
  });
};
// Switch a currency's rate mode: "daily" (Auto) or "corporate".
export const setOperatingCurrencyMode = (account_id, id, mode) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/set-mode/`,
    data: { mode },
  });
};
// Set the manual corporate exchange rate for a currency.
export const setOperatingCurrencyRate = (account_id, id, rate) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/set-rate/`,
    data: { rate },
  });
};
// Re-pull an Auto-mode currency's exchange rate from the provider on demand.
export const refreshOperatingCurrencyRate = (account_id, id) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/${id}/refresh-rate/`,
  });
};
// Bulk-switch every non-reference currency to a rate mode in one call.
export const bulkSetOperatingCurrencyMode = (account_id, mode) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-currency/bulk-set-mode/`,
    data: { mode },
  });
};
// List an account's operating languages (Rule 10 order: active A-Z, then retired A-Z).
export const getOperatingLanguages = (account_id, params = {}) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/operating-language/`,
    params,
  });
};
// Global active supported-language catalog the Add picker draws from (Rule 14); not account-filtered.
export const getSupportedLanguageCatalog = (account_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/operating-language/catalog/`,
  });
};
// Add an operating language from the global supported-language catalog (Rule 4).
export const addOperatingLanguage = (account_id, data) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-language/add/`,
    data,
  });
};
// Retire / Reactivate an operating language (POST actions per Rule 4 / Rule 13).
export const retireOperatingLanguage = (account_id, id) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-language/${id}/retire/`,
  });
};
export const reactivateOperatingLanguage = (account_id, id) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-language/${id}/reactivate/`,
  });
};
export const updateOperatingCountry = (account_id, id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.ACCOUNTList}${account_id}/operating-country/${id}/`,
    data: data,
  });
  return result;
};
export const getCurrencies = () => {
  let result = axios()({
    method: "get",
    url: `${API.COMMON_V2}currencies/`,
    params: { page: 1, size: 500 },
  });
  return result;
};
export const getCountryList = (params = {}) => {
  const defaultParams = { limit: 100, offset: 0, page: 1, size: 100 };
  const merged =
    params != null && typeof params === "object" ? { ...defaultParams, ...params } : defaultParams;
  let result = axios()({
    method: "get",
    url: `${API.COMMON_V2}country/`,
    params: merged,
  });
  return result;
};

export const addOperatingCountry = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/operating-country/`,
    data: data,
  });
  return result;
};

export const partialUpdateOperatingCountry = (account_id, id, data) => {
  let result = axios()({
    method: "PATCH",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/operating-country/${id}/`,
    data: data,
  });
  return result;
};

export const getUserChatter = (account_id, access_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.COMMON_V2}account/${account_id}/access/${access_id}/chatter/`,
    params: { page: page },
  });
  return result;
};

export const exchangeImpersonation = (code) =>
  createAxiosInstance(false)({
    method: "post",
    url: API.IMPERSONATE_EXCHANGE,
    data: { code },
  });

export const endImpersonation = () =>
  axios()({
    method: "post",
    url: API.IMPERSONATE_END,
  });

export const startViewAs = ({ accessId }) =>
  axios()({
    method: "post",
    url: API.VIEW_AS_START,
    data: { access_id: accessId },
  });

export const exchangeViewAs = (code) =>
  createAxiosInstance(false)({
    method: "post",
    url: API.VIEW_AS_EXCHANGE,
    data: { code },
  });

/** @param {string} [tokenOverride] use after admin context was already restored */
export const stopViewAs = (tokenOverride) =>
  createAxiosInstance(true, {
    headers: tokenOverride ? { Authorization: `Token ${tokenOverride}` } : {},
  })({
    method: "post",
    url: API.VIEW_AS_STOP,
  });

export const startPreview = ({ employeeId }) =>
  axios()({
    method: "post",
    url: API.PREVIEW_START,
    data: { employee_id: employeeId },
  });

export const exchangePreview = (code) =>
  createAxiosInstance(false)({
    method: "post",
    url: API.PREVIEW_EXCHANGE,
    data: { code },
  });

/** @param {string} [tokenOverride] use after admin context was already restored */
export const stopPreview = (tokenOverride) =>
  createAxiosInstance(true, {
    headers: tokenOverride ? { Authorization: `Token ${tokenOverride}` } : {},
  })({
    method: "post",
    url: API.PREVIEW_STOP,
  });
